// =============================================================================
// CalibrationDuel — second-step A/B refinement for an already-calibrated clone
//
// Why this exists: the first calibration pass LOCKS IN whatever the user
// swiped ✓ on. If some of those approved sentences were weak ("Scheiss-Texte"),
// they keep steering every send and the user is stuck. The duel un-sticks it:
// for each approved sentence (the CHAMPION) the backend writes a sharper
// CHALLENGER in the same voice. We show them blind as A vs B and the user
// picks A / Equal / B. Winning challengers replace their champions; losers are
// dropped. After the round we re-distill the preference profile.
//
// Blind A/B (we randomise which side is the new one and never label them
// "old/new") removes the status-quo bias so the pick reflects which sentence
// is genuinely better, not which one the user recognises.
// =============================================================================

import React, { useCallback, useMemo, useRef, useState } from "react";
import { CALIBRATION_CATEGORIES } from "./cloneUtils";
import { useCalibration } from "./useCalibration";
import { renderSlotted } from "./renderSlotted";

const CATEGORY_BY_ID = CALIBRATION_CATEGORIES.reduce((m, c) => {
  m[c.id] = c;
  return m;
}, {});

// Stable random side assignment per duel: returns true when the CHALLENGER
// should be shown on side A (left). Decided once at fetch time so re-renders
// never reshuffle the cards mid-decision.
const assignChallengerToA = () => Math.random() < 0.5;

const CalibrationDuel = ({ userId, cloneId, clone, language = "auto", onDone }) => {
  const {
    fetchDuels,
    submitDuelVerdicts,
    distillPreferences,
    generating,
    error,
    errorKind,
    clearError,
  } = useCalibration(userId, { mode: "body" });

  // phase: 'intro' | 'dueling' | 'saving' | 'done'
  const [phase, setPhase] = useState("intro");
  const [duels, setDuels] = useState([]); // [{ id, category, champion, challenger, challengerOnA }]
  const [idx, setIdx] = useState(0);
  const verdictsRef = useRef([]);
  const [swapped, setSwapped] = useState(0); // count of challenger-wins, for the summary

  const calibration = clone?.calibration;

  // How many approved "champions" exist — drives the intro copy + disabled CTA.
  const championCount = useMemo(() => {
    const cats = calibration?.categories || {};
    return Object.values(cats).reduce(
      (n, c) => n + (Array.isArray(c?.approved) ? c.approved.length : 0),
      0,
    );
  }, [calibration]);

  const start = useCallback(async () => {
    clearError();
    verdictsRef.current = [];
    setSwapped(0);
    setIdx(0);
    const result = await fetchDuels({ cloneId, language, maxDuels: 30 });
    if (!result) return; // error surfaced by the hook
    setDuels(
      result.map((d) => ({ ...d, challengerOnA: assignChallengerToA() })),
    );
    setPhase("dueling");
  }, [fetchDuels, cloneId, language, clearError]);

  const finish = useCallback(async () => {
    setPhase("saving");
    const verdicts = verdictsRef.current;
    if (verdicts.some((v) => v.choice === "challenger")) {
      await submitDuelVerdicts({ cloneId, currentCalibration: calibration, verdicts });
      // Re-distill so the new champions reshape the style profile immediately.
      await distillPreferences({ cloneId });
    }
    setPhase("done");
  }, [submitDuelVerdicts, distillPreferences, cloneId, calibration]);

  // Record a pick for the current duel and advance. `pickedSide` is 'A' | 'B'
  // | 'equal'. We translate the side into champion/challenger using the per-
  // duel randomised mapping so the stored verdict is unambiguous.
  const pick = useCallback(
    (pickedSide) => {
      const duel = duels[idx];
      if (!duel) return;
      let choice;
      if (pickedSide === "equal") {
        choice = "equal";
      } else {
        const challengerPicked =
          (pickedSide === "A" && duel.challengerOnA) ||
          (pickedSide === "B" && !duel.challengerOnA);
        choice = challengerPicked ? "challenger" : "champion";
      }
      verdictsRef.current.push({
        category: duel.category,
        championText: duel.champion?.text || "",
        challengerText: duel.challenger?.text || "",
        choice,
      });
      if (choice === "challenger") setSwapped((n) => n + 1);

      const next = idx + 1;
      if (next >= duels.length) {
        finish();
      } else {
        setIdx(next);
      }
    },
    [duels, idx, finish],
  );

  // ---- INTRO ----
  if (phase === "intro") {
    return (
      <div className="cca-duel">
        <div className="cca-duel-intro">
          <div className="cca-duel-intro-orb">
            <i className="fas fa-code-compare" />
          </div>
          <h4 className="cca-duel-intro-title">Refine your calibration (A/B)</h4>
          <p className="cca-duel-intro-sub">
            We pit each of your approved sentences against a fresh, sharper
            alternative in your voice. Pick the better one — winners replace the
            old version, so you can fix anything you locked in too early.
          </p>
          <div className="cca-duel-intro-meta">
            <span><i className="fas fa-layer-group" /> {championCount} approved to refine</span>
            <span><i className="fas fa-bolt" /> up to 30 quick A/B picks</span>
          </div>
          <button
            type="button"
            className="cca-duel-start-btn"
            onClick={start}
            disabled={generating || championCount === 0}
          >
            {generating ? (
              <><i className="fas fa-spinner fa-spin" /> Building challengers…</>
            ) : (
              <><i className="fas fa-code-compare" /> Start A/B refinement</>
            )}
          </button>
          {championCount === 0 && (
            <p className="cca-duel-empty-hint">
              Run a normal calibration round first — there are no approved
              sentences to refine yet.
            </p>
          )}
          {error && (
            <div className={`cca-calib-error cca-calib-error-${errorKind || "unknown"}`} role="alert">
              <i className="fas fa-triangle-exclamation" />
              <div className="cca-calib-error-body">
                <div className="cca-calib-error-msg">{error}</div>
              </div>
            </div>
          )}
          {onDone && (
            <button type="button" className="cca-duel-cancel" onClick={onDone}>
              Back to swipe deck
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---- SAVING ----
  if (phase === "saving") {
    return (
      <div className="cca-duel">
        <div className="cca-duel-saving">
          <i className="fas fa-spinner fa-spin" />
          <p>Saving your picks and re-learning your style…</p>
        </div>
      </div>
    );
  }

  // ---- DONE ----
  if (phase === "done") {
    return (
      <div className="cca-duel">
        <div className="cca-duel-done">
          <div className="cca-duel-done-icon"><i className="fas fa-circle-check" /></div>
          <h4>Refinement saved</h4>
          <p>
            {swapped > 0 ? (
              <>You upgraded <strong>{swapped}</strong> sentence{swapped === 1 ? "" : "s"}. Your style profile has been re-learned around the winners.</>
            ) : (
              <>You kept all your current sentences — nothing changed. Your calibration is solid as is.</>
            )}
          </p>
          <div className="cca-duel-done-actions">
            <button type="button" className="cca-duel-start-btn" onClick={start} disabled={generating}>
              <i className="fas fa-rotate" /> Another A/B round
            </button>
            {onDone && (
              <button type="button" className="cca-duel-cancel" onClick={onDone}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- DUELING ----
  const duel = duels[idx];
  if (!duel) return null;
  const cat = CATEGORY_BY_ID[duel.category];
  const textA = duel.challengerOnA ? duel.challenger?.text : duel.champion?.text;
  const textB = duel.challengerOnA ? duel.champion?.text : duel.challenger?.text;

  return (
    <div className="cca-duel">
      <div className="cca-duel-progress">
        Duel {idx + 1} of {duels.length}
        <div className="cca-duel-progress-bar">
          <span style={{ width: `${((idx + 1) / duels.length) * 100}%` }} />
        </div>
      </div>

      <div className="cca-duel-beat">
        <i className={`fas ${cat?.icon || "fa-tag"}`} /> {cat?.label || duel.category}
        <span className="cca-duel-beat-hint">Which one sounds more like you?</span>
      </div>

      <div className="cca-duel-arena">
        <button
          type="button"
          className="cca-duel-card cca-duel-card--a"
          onClick={() => pick("A")}
        >
          <span className="cca-duel-card-tag">A</span>
          <p className="cca-duel-card-text">{renderSlotted(textA)}</p>
          <span className="cca-duel-card-pick"><i className="fas fa-hand-pointer" /> Pick A</span>
        </button>

        <button
          type="button"
          className="cca-duel-card cca-duel-card--b"
          onClick={() => pick("B")}
        >
          <span className="cca-duel-card-tag">B</span>
          <p className="cca-duel-card-text">{renderSlotted(textB)}</p>
          <span className="cca-duel-card-pick"><i className="fas fa-hand-pointer" /> Pick B</span>
        </button>
      </div>

      <button type="button" className="cca-duel-equal" onClick={() => pick("equal")}>
        <i className="fas fa-equals" /> About the same — keep my current one
      </button>
    </div>
  );
};

export default CalibrationDuel;
